import {FC, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Badge} from "@interstate/components/Badge";
import {useInterstateToken} from "@interstate/components/InterstateThemeProvider";
import {BadgeBar} from "../../../../../../badges";
import {SummaryCard} from "../../../../create-hierarchy/steps/common";
import {useCreateNewEntity} from "../../context";
import {createNewEntityBadgeMetadata} from "./CreateNewEntityBadgeMetadata";
import "../../../../create-hierarchy/steps/common/BuilderStep.scss";
import "./CreateNewEntityResultsStep.scss";

export interface CreateNewEntityResultsStepProps {
  onCreateAnother: () => void;
  onViewEntity?: (entityId: string) => void;
  onReturnToDashboard: () => void;
}

/**
 * Results step of the CreateNewEntityStepper - displays creation results and provides navigation options.
 * Shows success/failure states with appropriate UI feedback and action buttons.
 *
 * Follows the same pattern as the hierarchy stepper ResultsStep component.
 */
export const CreateNewEntityResultsStep: FC<
  CreateNewEntityResultsStepProps
> = ({onCreateAnother, onViewEntity, onReturnToDashboard}) => {
  const {t} = useTranslation();
  const token = useInterstateToken();
  const controller = useCreateNewEntity();

  // Get badge metadata based on entity status
  const {labelKey, variant, startIcon, endIcon} = createNewEntityBadgeMetadata(
    token,
    controller.entityStatus
  );

  // Use the actual entity ID from the GraphQL response
  const entityId = controller.createdEntityId;
  const isSuccess = controller.entityStatus === "created";

  // Summary based on entity status
  const summary = useMemo(() => {
    const created = isSuccess ? 1 : 0;
    const failed = controller.entityStatus === "failed" ? 1 : 0;
    const inProgress =
      controller.entityStatus === "in-progress" ||
      controller.entityStatus === "queued"
        ? 1
        : 0;

    return {created, failed, inProgress};
  }, [isSuccess, controller.entityStatus]);

  return (
    <section
      data-testid="create-new-entity-results-step"
      className="hierarchy-builder-step create-new-entity-results-step">
      {/* Summary Cards Section */}
      <section data-testid="summary-section" className="summary-section">
        <SummaryCard
          baseKey="create-new-entity.result-summary.created"
          count={summary.created}
        />
      </section>

      {/* Entity Details Section */}
      <section data-testid="hierarchy-section" className="hierarchy-section">
        <div className="create-new-entity-item">
          <div className="entity-info">
            <span className="entity-name">{controller.formData.dbaName}</span>
          </div>
          <BadgeBar>
            {isSuccess && entityId && onViewEntity ? (
              <button
                type="button"
                onClick={() => onViewEntity(entityId)}
                className="badge-button">
                <Badge
                  id={`create-new-entity-badge-${entityId || "no-id"}`}
                  data-testid={`create-new-entity-badge-${entityId || "no-id"}`}
                  className="create-new-entity-badge"
                  variant={variant}
                  startIcon={startIcon}
                  endIcon={endIcon}>
                  <span className="create-new-entity-badge-label">
                    {t(labelKey!)}
                  </span>
                </Badge>
              </button>
            ) : (
              <Badge
                id={`create-new-entity-badge-${entityId || "no-id"}`}
                data-testid={`create-new-entity-badge-${entityId || "no-id"}`}
                className="create-new-entity-badge"
                variant={variant}
                startIcon={startIcon}
                endIcon={endIcon}>
                <span className="create-new-entity-badge-label">
                  {t(labelKey!)}
                </span>
              </Badge>
            )}
          </BadgeBar>
        </div>
      </section>

      {/* Error Details Section (only show if failed) */}
      {controller.entityStatus === "failed" && controller.submitError && (
        <section
          data-testid="error-section"
          className="error-section hierarchy-section">
          <div className="error-header">
            <h3>{t("create-new-entity.results.error.details.title")}</h3>
          </div>
          <div className="error-message">{controller.submitError}</div>
        </section>
      )}
    </section>
  );
};
